import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import Link from "@tiptap/extension-link";
import { Extension } from "@tiptap/core";
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Popover,
  Typography,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  HorizontalRule,
  SmartButton,
  TextFields,
  DataObject,
  FormatColorText,
  FontDownload,
} from "@mui/icons-material";
import PlaceholderExtension from "./PlaceholderExtension";
import { ButtonBlock, PreheaderBlock, DividerBlock } from "./BlockExtensions";
import { colors } from "../../theme/modernTheme";

// Custom FontSize extension
const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

/**
 * RichTextEditor Component
 * WYSIWYG editor for email templates with formatting, placeholders, and links
 * Validates: Requirements 2.1-2.5, 3.2, 3.4, 4.1, 4.4, 17.1-17.5
 */
const RichTextEditor = React.forwardRef(
  ({ value = "", onChange, placeholders = [] }, ref) => {
    const [showHtml, setShowHtml] = useState(false);
    const [htmlContent, setHtmlContent] = useState("");
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkText, setLinkText] = useState("");
    const [linkUrlError, setLinkUrlError] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
    const [buttonUrl, setButtonUrl] = useState("");
    const [buttonText, setButtonText] = useState("Click Here");
    const [buttonUrlError, setButtonUrlError] = useState("");
    const [preheaderDialogOpen, setPreheaderDialogOpen] = useState(false);
    const [preheaderText, setPreheaderText] = useState("");
    const [placeholderMenuAnchor, setPlaceholderMenuAnchor] = useState(null);
    const [blockMenuAnchor, setBlockMenuAnchor] = useState(null);
    const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState(null);
    const [fontColorAnchor, setFontColorAnchor] = useState(null);
    const [fontFamilyMenuAnchor, setFontFamilyMenuAnchor] = useState(null);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // Disable built-in extensions we're replacing
          link: false,
        }),
        Underline,
        TextStyle,
        Color,
        FontSize,
        FontFamily.configure({
          types: ["textStyle"],
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
        PlaceholderExtension,
        ButtonBlock,
        PreheaderBlock,
        DividerBlock,
      ],
      content: value,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        if (onChange) {
          onChange(html);
        }
      },
      editable: true,
    });

    // Disable editor when dialog is open
    React.useEffect(() => {
      if (editor) {
        editor.setEditable(!isDialogOpen);
      }
    }, [editor, isDialogOpen]);

    React.useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value);
      }
    }, [value, editor]);

    // Expose methods via ref (Bug #6)
    React.useImperativeHandle(ref, () => ({
      focus: () => {
        if (editor) {
          editor.commands.focus();
        }
      },
      insertPlaceholder: (placeholder) => {
        if (editor) {
          editor.commands.insertPlaceholder({
            token: placeholder.token,
            label: placeholder.label,
          });
        }
      },
    }));

    const handleHtmlToggle = () => {
      if (!showHtml) {
        // Switching to HTML view
        setHtmlContent(editor.getHTML());
        setShowHtml(true);
      } else {
        // Switching back to WYSIWYG
        editor.commands.setContent(htmlContent);
        setShowHtml(false);
      }
    };

    const handleHtmlChange = (e) => {
      setHtmlContent(e.target.value);
    };

    const handleInsertLink = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to);
      setLinkText(selectedText || "");
      setLinkUrl("");
      setLinkUrlError("");
      setIsDialogOpen(true);
      // Use setTimeout to ensure dialog opens after any pending focus operations
      setTimeout(() => {
        setLinkDialogOpen(true);
      }, 50);
    };

    const handleLinkSubmit = () => {
      if (linkUrl) {
        // Validate URL protocol
        if (!linkUrl.startsWith("http://") && !linkUrl.startsWith("https://")) {
          setLinkUrlError("URL must start with http:// or https://");
          return;
        }

        // Close dialog first
        setLinkDialogOpen(false);
        setIsDialogOpen(false);

        // Then insert link after a brief delay
        setTimeout(() => {
          if (linkText) {
            editor
              .chain()
              .focus()
              .insertContent(`<a href="${linkUrl}">${linkText}</a>`)
              .run();
          } else {
            editor.chain().focus().setLink({ href: linkUrl }).run();
          }
          setLinkUrl("");
          setLinkText("");
          setLinkUrlError("");
        }, 100);
      } else {
        setLinkDialogOpen(false);
        setIsDialogOpen(false);
        setLinkUrl("");
        setLinkText("");
        setLinkUrlError("");
      }
    };

    const handleInsertPlaceholder = (placeholder) => {
      if (editor) {
        editor.commands.insertPlaceholder({
          token: placeholder.token,
          label: placeholder.label,
        });
      }
      setPlaceholderMenuAnchor(null);
    };

    const handleInsertButton = () => {
      // Disable editor to prevent it from capturing keyboard events
      editor.setEditable(false);
      setButtonUrl("");
      setButtonText("Click Here");
      setButtonUrlError("");
      setButtonDialogOpen(true);
    };

    const handleButtonSubmit = () => {
      if (buttonUrl) {
        // Validate URL protocol
        if (
          !buttonUrl.startsWith("http://") &&
          !buttonUrl.startsWith("https://")
        ) {
          setButtonUrlError("URL must start with http:// or https://");
          return;
        }

        editor.commands.insertButton({
          href: buttonUrl,
          buttonText: buttonText,
        });
      }
      setButtonDialogOpen(false);
      setButtonUrlError("");
      // Re-enable and refocus editor after closing dialog
      setTimeout(() => {
        editor.setEditable(true);
        editor.commands.focus();
      }, 100);
    };

    const handlePreheaderSubmit = () => {
      if (preheaderText) {
        editor.commands.insertPreheader({
          text: preheaderText,
        });
      }
      setPreheaderDialogOpen(false);
      // Re-enable and refocus editor after closing dialog
      setTimeout(() => {
        editor.setEditable(true);
        editor.commands.focus();
      }, 100);
    };

    const handleInsertDivider = () => {
      editor.commands.insertDivider();
      setBlockMenuAnchor(null);
    };

    if (!editor) {
      return null;
    }

    return (
      <Paper
        elevation={2}
        sx={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}>
        {/* Toolbar */}
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}>
          {/* Text Formatting */}
          <ToggleButtonGroup size="small" aria-label="text formatting">
            <ToggleButton
              value="bold"
              selected={editor.isActive("bold")}
              onClick={() => editor.chain().focus().toggleBold().run()}
              aria-label="bold">
              <Tooltip title="Bold (Ctrl+B)">
                <FormatBold />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="italic"
              selected={editor.isActive("italic")}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              aria-label="italic">
              <Tooltip title="Italic (Ctrl+I)">
                <FormatItalic />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="underline"
              selected={editor.isActive("underline")}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              aria-label="underline">
              <Tooltip title="Underline (Ctrl+U)">
                <FormatUnderlined />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Font Size */}
          <Tooltip title="Font Size">
            <IconButton
              size="small"
              onClick={(e) => setFontSizeMenuAnchor(e.currentTarget)}
              aria-label="font size">
              <TextFields />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={fontSizeMenuAnchor}
            open={Boolean(fontSizeMenuAnchor)}
            onClose={() => setFontSizeMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("12px").run();
                setFontSizeMenuAnchor(null);
              }}>
              12px - Small
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("14px").run();
                setFontSizeMenuAnchor(null);
              }}>
              14px - Normal
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("16px").run();
                setFontSizeMenuAnchor(null);
              }}>
              16px - Medium
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("18px").run();
                setFontSizeMenuAnchor(null);
              }}>
              18px - Large
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("24px").run();
                setFontSizeMenuAnchor(null);
              }}>
              24px - Extra Large
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().setFontSize("32px").run();
                setFontSizeMenuAnchor(null);
              }}>
              32px - Huge
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().unsetFontSize().run();
                setFontSizeMenuAnchor(null);
              }}>
              Reset to Default
            </MenuItem>
          </Menu>

          <Divider orientation="vertical" flexItem />

          {/* Text Alignment (Bug #7: Fixed to apply only to selected blocks) */}
          <ToggleButtonGroup size="small" aria-label="text alignment">
            <ToggleButton
              value="left"
              selected={editor.isActive({ textAlign: "left" })}
              onClick={() => {
                // Only apply to current selection/paragraph
                editor.chain().focus().setTextAlign("left").run();
              }}
              aria-label="align left">
              <Tooltip title="Align Left">
                <FormatAlignLeft />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="center"
              selected={editor.isActive({ textAlign: "center" })}
              onClick={() => {
                // Only apply to current selection/paragraph
                editor.chain().focus().setTextAlign("center").run();
              }}
              aria-label="align center">
              <Tooltip title="Align Center">
                <FormatAlignCenter />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="right"
              selected={editor.isActive({ textAlign: "right" })}
              onClick={() => {
                // Only apply to current selection/paragraph
                editor.chain().focus().setTextAlign("right").run();
              }}
              aria-label="align right">
              <Tooltip title="Align Right">
                <FormatAlignRight />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Lists */}
          <ToggleButtonGroup size="small" aria-label="lists">
            <ToggleButton
              value="bulletList"
              selected={editor.isActive("bulletList")}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              aria-label="bullet list">
              <Tooltip title="Bullet List">
                <FormatListBulleted />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="orderedList"
              selected={editor.isActive("orderedList")}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              aria-label="numbered list">
              <Tooltip title="Numbered List">
                <FormatListNumbered />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Insert Elements */}
          <Tooltip title="Insert Link">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleInsertLink(e);
              }}
              aria-label="insert link">
              <LinkIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Font Color">
            <IconButton
              size="small"
              onClick={(e) => setFontColorAnchor(e.currentTarget)}
              aria-label="font color">
              <FormatColorText />
            </IconButton>
          </Tooltip>

          <Tooltip title="Font Family">
            <IconButton
              size="small"
              onClick={(e) => setFontFamilyMenuAnchor(e.currentTarget)}
              aria-label="font family">
              <FontDownload />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Placeholder">
            <IconButton
              size="small"
              onClick={(e) => setPlaceholderMenuAnchor(e.currentTarget)}
              aria-label="insert placeholder">
              <DataObject />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Block">
            <IconButton
              size="small"
              onClick={(e) => setBlockMenuAnchor(e.currentTarget)}
              aria-label="insert block">
              <SmartButton />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Editor Content */}
        <Box sx={{ minHeight: 500, maxHeight: 600, overflow: "auto", p: 2 }}>
          {showHtml ? (
            <TextField
              multiline
              fullWidth
              value={htmlContent}
              onChange={handleHtmlChange}
              variant="outlined"
              minRows={15}
              maxRows={25}
              inputProps={{
                form: {},
              }}
              sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
            />
          ) : (
            <EditorContent
              editor={editor}
              style={{
                minHeight: "350px",
                outline: "none",
              }}
            />
          )}
        </Box>

        {/* Font Color Picker */}
        <Popover
          open={Boolean(fontColorAnchor)}
          anchorEl={fontColorAnchor}
          onClose={() => setFontColorAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 1 }}>
              Choose Font Color
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 1,
              }}>
              {[
                { name: "Black", value: "#000000" },
                { name: "Dark Gray", value: "#4B5563" },
                { name: "Gray", value: "#9CA3AF" },
                { name: "Red", value: "#DC2626" },
                { name: "Orange", value: "#EA580C" },
                { name: "Yellow", value: "#CA8A04" },
                { name: "Green", value: "#16A34A" },
                { name: "Blue", value: "#2563EB" },
                { name: "Indigo", value: "#4F46E5" },
                { name: "Purple", value: "#9333EA" },
                { name: "Pink", value: "#DB2777" },
                { name: "White", value: "#FFFFFF" },
              ].map((color) => (
                <Tooltip key={color.value} title={color.name}>
                  <Box
                    onClick={() => {
                      editor.chain().focus().setColor(color.value).run();
                      setFontColorAnchor(null);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color.value,
                      border: "2px solid #E5E7EB",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#4F46E5",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s",
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
            <Button
              size="small"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setFontColorAnchor(null);
              }}
              sx={{ mt: 1 }}>
              Reset Color
            </Button>
          </Box>
        </Popover>

        {/* Font Family Menu */}
        <Menu
          anchorEl={fontFamilyMenuAnchor}
          open={Boolean(fontFamilyMenuAnchor)}
          onClose={() => setFontFamilyMenuAnchor(null)}>
          {[
            { name: "Arial", value: "Arial, sans-serif" },
            { name: "Times New Roman", value: "Times New Roman, serif" },
            { name: "Georgia", value: "Georgia, serif" },
            { name: "Verdana", value: "Verdana, sans-serif" },
            { name: "Courier New", value: "Courier New, monospace" },
            { name: "Trebuchet MS", value: "Trebuchet MS, sans-serif" },
            { name: "Comic Sans MS", value: "Comic Sans MS, cursive" },
            { name: "Impact", value: "Impact, sans-serif" },
          ].map((font) => (
            <MenuItem
              key={font.value}
              onClick={() => {
                editor.chain().focus().setFontFamily(font.value).run();
                setFontFamilyMenuAnchor(null);
              }}
              sx={{ fontFamily: font.value }}>
              {font.name}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              editor.chain().focus().unsetFontFamily().run();
              setFontFamilyMenuAnchor(null);
            }}>
            Reset Font
          </MenuItem>
        </Menu>

        {/* Placeholder Menu */}
        <Menu
          anchorEl={placeholderMenuAnchor}
          open={Boolean(placeholderMenuAnchor)}
          onClose={() => setPlaceholderMenuAnchor(null)}>
          {placeholders.length === 0 ? (
            <MenuItem disabled>No placeholders available</MenuItem>
          ) : (
            placeholders.map((placeholder, index) => (
              <MenuItem
                key={index}
                onClick={() => handleInsertPlaceholder(placeholder)}>
                {placeholder.label}
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Block Menu */}
        <Menu
          anchorEl={blockMenuAnchor}
          open={Boolean(blockMenuAnchor)}
          onClose={() => setBlockMenuAnchor(null)}>
          <MenuItem onClick={handleInsertDivider}>
            <HorizontalRule sx={{ mr: 1 }} /> Divider
          </MenuItem>
          <MenuItem onClick={handleInsertButton}>
            <SmartButton sx={{ mr: 1 }} /> Button
          </MenuItem>
        </Menu>

        {/* Link Dialog */}
        <Dialog
          open={linkDialogOpen}
          onClose={() => {
            setLinkDialogOpen(false);
            setIsDialogOpen(false);
            setLinkUrl("");
            setLinkText("");
          }}
          maxWidth="sm"
          fullWidth
          disableRestoreFocus
          disableEnforceFocus
          disableAutoFocus={false}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 500,
              color: colors.text.primary,
              p: 2,
            }}>
            Insert Link
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              autoFocus
              label="URL"
              type="text"
              fullWidth
              variant="outlined"
              value={linkUrl}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setLinkUrlError("");
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleLinkSubmit();
                }
              }}
              onKeyPress={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              placeholder="https://example.com"
              error={!!linkUrlError}
              helperText={linkUrlError}
              sx={{ mb: 2, mt: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                form: {},
              }}
            />
            <TextField
              size="small"
              label="Link Text (optional)"
              type="text"
              fullWidth
              variant="outlined"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleLinkSubmit();
                }
              }}
              onKeyPress={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              placeholder="Enter link text"
              inputProps={{
                maxLength: 100,
                form: {},
              }}
              helperText={`${linkText.length}/100`}
              FormHelperTextProps={{
                sx: { textAlign: "right", marginRight: 0 },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{ p: 1, borderTop: `1px solid ${colors.border.main}` }}>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={() => {
                setLinkDialogOpen(false);
                setIsDialogOpen(false);
                setLinkUrl("");
                setLinkText("");
                setLinkUrlError("");
              }}>
              Cancel
            </Button>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={handleLinkSubmit}
              variant="contained">
              Insert
            </Button>
          </DialogActions>
        </Dialog>

        {/* Button Dialog */}
        <Dialog
          open={buttonDialogOpen}
          onClose={() => {
            setButtonDialogOpen(false);
            setButtonUrl("");
            setButtonText("Click Here");
            // Re-enable and refocus editor after closing dialog
            setTimeout(() => {
              editor.setEditable(true);
              editor.commands.focus();
            }, 100);
          }}
          maxWidth="sm"
          fullWidth
          disableRestoreFocus
          disableEnforceFocus
          disableAutoFocus={false}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 500,
              color: colors.text.primary,
              p: 2,
            }}>
            Insert Button
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              autoFocus
              label="Button URL"
              type="url"
              fullWidth
              variant="outlined"
              value={buttonUrl}
              onChange={(e) => {
                setButtonUrl(e.target.value);
                setButtonUrlError("");
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleButtonSubmit();
                }
              }}
              onKeyUp={(e) => e.stopPropagation()}
              placeholder="https://example.com"
              error={!!buttonUrlError}
              helperText={buttonUrlError}
              sx={{ mb: 2, mt: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                form: {},
              }}
            />
            <TextField
              size="small"
              label="Button Text"
              type="text"
              fullWidth
              variant="outlined"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleButtonSubmit();
                }
              }}
              onKeyUp={(e) => e.stopPropagation()}
              placeholder="Click Here"
              inputProps={{
                maxLength: 100,
                form: {},
              }}
              helperText={`${buttonText.length}/100`}
              FormHelperTextProps={{
                sx: { textAlign: "right", marginRight: 0 },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{ p: 1, borderTop: `1px solid ${colors.border.main}` }}>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={() => {
                setButtonDialogOpen(false);
                setButtonUrl("");
                setButtonText("Click Here");
                setButtonUrlError("");
                // Re-enable and refocus editor after closing dialog
                setTimeout(() => {
                  editor.setEditable(true);
                  editor.commands.focus();
                }, 100);
              }}>
              Cancel
            </Button>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={handleButtonSubmit}
              variant="contained">
              Insert
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preheader Dialog */}
        <Dialog
          open={preheaderDialogOpen}
          onClose={() => {
            setPreheaderDialogOpen(false);
            setPreheaderText("");
            // Re-enable and refocus editor after closing dialog
            setTimeout(() => {
              editor.setEditable(true);
              editor.commands.focus();
            }, 100);
          }}
          maxWidth="sm"
          fullWidth
          disableRestoreFocus
          disableEnforceFocus
          disableAutoFocus={false}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 500,
              color: colors.text.primary,
              p: 2,
            }}>
            Insert Preheader
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              autoFocus
              label="Preheader Text"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={preheaderText}
              onChange={(e) => setPreheaderText(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onKeyPress={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              placeholder="This text will appear in email previews"
              sx={{ mt: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                form: {},
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{ p: 1, borderTop: `1px solid ${colors.border.main}` }}>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={() => {
                setPreheaderDialogOpen(false);
                setPreheaderText("");
                // Re-enable and refocus editor after closing dialog
                setTimeout(() => {
                  editor.setEditable(true);
                  editor.commands.focus();
                }, 100);
              }}>
              Cancel
            </Button>
            <Button
              sx={{ borderRadius: "4px" }}
              onClick={handlePreheaderSubmit}
              variant="contained">
              Insert
            </Button>
          </DialogActions>
        </Dialog>

        {/* Editor Styles */}
        <style>
          {`
          .ProseMirror {
            outline: none;
          }
          
          .ProseMirror p {
            margin: 0.5em 0;
          }
          
          .ProseMirror ul,
          .ProseMirror ol {
            padding-left: 1.5em;
          }
          
          .ProseMirror a {
            color: #1976d2;
            text-decoration: underline;
            cursor: pointer;
          }
          
          .ProseMirror img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 10px 0;
          }
          
          .placeholder-token {
            display: inline-block;
            margin: 0 2px;
          }
          
          .email-button-wrapper {
            text-align: center;
            margin: 20px 0;
          }
          
          .email-button {
            display: inline-block;
            padding: 12px 24px;
            background-color: #1976d2;
            color: #ffffff;
            text-decoration: none;
            border-radius: 4px;
            font-weight: 500;
          }
          
          .preheader {
            display: none;
            max-height: 0;
            overflow: hidden;
          }
          
          .email-divider {
            border: none;
            border-top: 2px solid #e0e0e0;
            margin: 20px 0;
          }
        `}
        </style>
      </Paper>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
